/**
 * DemoDatabase クラス（Dexie + EntityTable<T> × 25）。
 *
 * 元はこのファイルに型定義（700 行超）と migration 16 段階の history が同居していた。
 * 型定義は `./types` に移動し、migration history は最新状態の 1 version に集約することで、
 * route -api / -query / components が `@/mocks/db/schema` を import するときに
 * tsgo に評価させる Dexie generic Table の instantiation を最小化している。
 *
 * このファイルを import するのは「`db` の値が必要な mock repository 層」だけに留め、
 * 型のみを参照する route -api / -query / components は `./types` を import する。
 *
 * 後方互換のため、`./types` から全ての型を re-export している。
 *
 * NOTE: `individualPortalSequence*` / `individualPortalAiChatSessions` /
 * `individualPortalUpcomingInteractions` という名前は歴史的経緯による。
 * 現在は v8 で `side` フィールドを追加しており、individual と cockpit の
 * 両サイドのデータを単一テーブルで保持している（`side` で絞り込み）。
 */

import Dexie, { type EntityTable, type Transaction } from 'dexie';
import type { OperationEventSnapshotRow } from './operationEventPayload';
import type { AttendanceAlert, AttendanceEmployee, AttendanceItem, AttendanceLogicComponent, AttendanceOpPackage, AttendanceOpPackageInstanceItem, AttendanceOpPackagePattern, AttendanceOperationActivity, AttendanceRecord, AttendanceRulePolicy, AttendanceStructure, AttendanceUnit, AttendanceUnitItem, AttendanceUnitLogicComponent, ClockingRecord, DeviceLicense, Establishment, EstablishmentEmployee, EstablishmentMembershipOverride, EstablishmentOrgUnit, EstablishmentPlace, LicenseViolationAlert, LicenseMember, LicenseActivity, LicenseAssignmentRule, LicenseSeat, LicenseRenewal, EmployeeClockingRecordAlert, EmployeeClockingRecordFile, EmployeePaySlipAlert, EmployeePaySlipDetailFile, EmployeePaySlipSummary, EmployeePayslipAttendance, IndividualPortalAiChatSession, IndividualPortalApproval, IndividualPortalAttendance, IndividualPortalRequest, IndividualPortalRequestForm, IndividualPortalSequenceDefinition, IndividualPortalSequenceInstance, IndividualPortalSequenceStepRecord, IndividualPortalTask, IndividualPortalUpcomingInteraction, MemberAccount, MemberCertification, MemberDevice, MemberEquipment, OperationPackage, PortalPerson, PortalPlace, PayrollAlert, PayrollAttendanceDashboardMetric, PayrollInputSnapshotField, PayrollItem, PayrollLogicComponent, PayrollOpPackage, PayrollOpPackageInstanceItem, PayrollOpPackagePattern, PayrollOverviewFact, PayrollStructure, PayrollUnit, TimeClockCorrection, PayrollUnitItem, PayrollUnitLogicComponent, PaySlip, PaySlipCalculationSnapshot, PaySlipFile, PaySlipFolder, PaySlipLayout, PaySlipLayoutStructure, PaySlipPdfMock, PaySlipRecord, PharmacyArea, PharmacyStore, Person, ShiftAlert, ShiftAssignment, ShiftStaff, UiPreference, WorkExperienceCompany, AttendanceSnapshot, JurisdictionTaxLine, SalaryLine, SalaryRecord, SalaryPaymentAllocation, SalaryWageAlert, SalaryWageStructureAssignmentRule, SchemeContribution, TaxCalculationBasis, WageItemVocabularyEntry } from './types';

// 後方互換のため全ての型を re-export（既存の `import { Foo } from "@/mocks/db/schema"` を壊さない）
export type * from './types';

// ─── Database ───────────────────────────────────────────────

export class DemoDatabase extends Dexie {
  people!: EntityTable<Person, 'id'>;
  attendanceRecords!: EntityTable<AttendanceRecord, 'id'>;
  clockingRecords!: EntityTable<ClockingRecord, 'id'>;
  attendanceAlerts!: EntityTable<AttendanceAlert, 'id'>;
  attendanceEmployees!: EntityTable<AttendanceEmployee, 'id'>;
  timeClockCorrections!: EntityTable<TimeClockCorrection, 'id'>;
  paySlips!: EntityTable<PaySlip, 'id'>;
  paySlipRecords!: EntityTable<PaySlipRecord, 'id'>;
  paySlipFolders!: EntityTable<PaySlipFolder, 'id'>;
  paySlipFiles!: EntityTable<PaySlipFile, 'id'>;
  employeePaySlipSummaries!: EntityTable<EmployeePaySlipSummary, 'id'>;
  employeePayslipAttendance!: EntityTable<EmployeePayslipAttendance, 'id'>;
  paySlipCalculationSnapshots!: EntityTable<PaySlipCalculationSnapshot, 'id'>;
  payrollItems!: EntityTable<PayrollItem, 'id'>;
  payrollInputSnapshotFields!: EntityTable<PayrollInputSnapshotField, 'id'>;
  payrollLogicComponents!: EntityTable<PayrollLogicComponent, 'id'>;
  attendanceItems!: EntityTable<AttendanceItem, 'id'>;
  attendanceLogicComponents!: EntityTable<AttendanceLogicComponent, 'id'>;
  attendanceStructures!: EntityTable<AttendanceStructure, 'id'>;
  payrollStructures!: EntityTable<PayrollStructure, 'id'>;
  payrollUnits!: EntityTable<PayrollUnit, 'id'>;
  payrollUnitItems!: EntityTable<PayrollUnitItem, 'id'>;
  payrollUnitLogicComponents!: EntityTable<PayrollUnitLogicComponent, 'id'>;
  attendanceUnits!: EntityTable<AttendanceUnit, 'id'>;
  attendanceUnitItems!: EntityTable<AttendanceUnitItem, 'id'>;
  attendanceUnitLogicComponents!: EntityTable<AttendanceUnitLogicComponent, 'id'>;
  dashboardMetrics!: EntityTable<PayrollAttendanceDashboardMetric, 'id'>;
  operationPackages!: EntityTable<OperationPackage, 'id'>;
  payrollGanttSnapshots!: EntityTable<OperationEventSnapshotRow, 'id'>;
  attendanceGanttSnapshots!: EntityTable<OperationEventSnapshotRow, 'id'>;
  attendanceOpPackages!: EntityTable<AttendanceOpPackage, 'id'>;
  attendanceOpPackagePatterns!: EntityTable<AttendanceOpPackagePattern, 'id'>;
  attendanceOpPackageInstanceItems!: EntityTable<AttendanceOpPackageInstanceItem, 'id'>;
  payrollOpPackages!: EntityTable<PayrollOpPackage, 'id'>;
  payrollOpPackagePatterns!: EntityTable<PayrollOpPackagePattern, 'id'>;
  payrollOpPackageInstanceItems!: EntityTable<PayrollOpPackageInstanceItem, 'id'>;
  attendanceRulePolicies!: EntityTable<AttendanceRulePolicy, 'id'>;
  payrollAlerts!: EntityTable<PayrollAlert, 'id'>;

  // pay slip 詳細 / clocking record 詳細の alerts / files タブ用（mock 専用）
  employeePaySlipAlerts!: EntityTable<EmployeePaySlipAlert, 'id'>;
  employeeClockingRecordAlerts!: EntityTable<EmployeeClockingRecordAlert, 'id'>;
  employeePaySlipDetailFiles!: EntityTable<EmployeePaySlipDetailFile, 'id'>;
  employeeClockingRecordFiles!: EntityTable<EmployeeClockingRecordFile, 'id'>;
  payrollOverviewFacts!: EntityTable<PayrollOverviewFact, 'id'>;
  attendanceOperationActivities!: EntityTable<AttendanceOperationActivity, 'id'>;
  individualPortalAttendance!: EntityTable<IndividualPortalAttendance, 'id'>;
  individualPortalTasks!: EntityTable<IndividualPortalTask, 'id'>;
  individualPortalApprovals!: EntityTable<IndividualPortalApproval, 'id'>;
  individualPortalRequests!: EntityTable<IndividualPortalRequest, 'id'>;
  individualPortalSequenceDefinitions!: EntityTable<IndividualPortalSequenceDefinition, 'id'>;
  individualPortalSequenceInstances!: EntityTable<IndividualPortalSequenceInstance, 'id'>;
  individualPortalSequenceStepRecords!: EntityTable<IndividualPortalSequenceStepRecord, 'id'>;
  individualPortalRequestForms!: EntityTable<IndividualPortalRequestForm, 'id'>;
  individualPortalAiChatSessions!: EntityTable<IndividualPortalAiChatSession, 'id'>;
  individualPortalUpcomingInteractions!: EntityTable<IndividualPortalUpcomingInteraction, 'id'>;
  uiPreferences!: EntityTable<UiPreference, 'key'>;
  paySlipPdfMocks!: EntityTable<PaySlipPdfMock, 'id'>;

  // 薬局シフト管理 mock（拠点 / エリア / スタッフ / 割当 / アラート）
  pharmacyAreas!: EntityTable<PharmacyArea, 'id'>;
  pharmacyStores!: EntityTable<PharmacyStore, 'id'>;
  shiftStaff!: EntityTable<ShiftStaff, 'id'>;
  shiftAssignments!: EntityTable<ShiftAssignment, 'id'>;
  shiftAlerts!: EntityTable<ShiftAlert, 'id'>;

  // 個人ポータル 照会リソース（拠点 / メンバー資格 / デバイス / アカウント / 備品）
  portalPersons!: EntityTable<PortalPerson, 'id'>;
  portalPlaces!: EntityTable<PortalPlace, 'id'>;
  memberCertifications!: EntityTable<MemberCertification, 'id'>;
  memberDevices!: EntityTable<MemberDevice, 'id'>;
  memberAccounts!: EntityTable<MemberAccount, 'id'>;
  memberEquipments!: EntityTable<MemberEquipment, 'id'>;

  // 職歴情報（Work Experience）タブ mock（メンバー詳細）
  workExperienceCompanies!: EntityTable<WorkExperienceCompany, 'id'>;

  // デバイス詳細「ライセンス」タブ mock（見せ球）
  deviceLicenses!: EntityTable<DeviceLicense, 'id'>;

  // ライセンス配下の違反集約アラート mock（見せ球）
  licenseViolationAlerts!: EntityTable<LicenseViolationAlert, 'id'>;

  // ライセンス配下タブ mock（見せ球）
  licenseMembers!: EntityTable<LicenseMember, 'id'>;
  licenseActivities!: EntityTable<LicenseActivity, 'id'>;
  licenseAssignmentRules!: EntityTable<LicenseAssignmentRule, 'id'>;
  licenseSeats!: EntityTable<LicenseSeat, 'id'>;
  licenseRenewals!: EntityTable<LicenseRenewal, 'id'>;

  // Salary & Wages（給与台帳カタログ E1〜E4c）
  salaryWageRecords!: EntityTable<SalaryRecord, 'id'>;
  salaryWageLines!: EntityTable<SalaryLine, 'id'>;
  salaryWageItemVocabulary!: EntityTable<WageItemVocabularyEntry, 'code'>;
  salaryWageAttendanceSnapshots!: EntityTable<AttendanceSnapshot, 'id'>;
  salaryWageTaxBases!: EntityTable<TaxCalculationBasis, 'id'>;
  salaryWageJurisdictionTaxLines!: EntityTable<JurisdictionTaxLine, 'id'>;
  salaryWageSchemeContributions!: EntityTable<SchemeContribution, 'id'>;
  salaryWagePaymentAllocations!: EntityTable<SalaryPaymentAllocation, 'id'>;

  // 事業場マスタ mock（給与・賃金 > 設定 > 組織 > 事業場）。places / orgUnits / employees は読み取り専用
  establishments!: EntityTable<Establishment, 'id'>;
  establishmentOverrides!: EntityTable<EstablishmentMembershipOverride, 'id'>;
  establishmentPlaces!: EntityTable<EstablishmentPlace, 'id'>;
  establishmentOrgUnits!: EntityTable<EstablishmentOrgUnit, 'id'>;
  establishmentEmployees!: EntityTable<EstablishmentEmployee, 'id'>;

  // Salary & Wages ルール&ポリシー: 給与計算体系アサインメントルール mock
  salaryWageStructureAssignmentRules!: EntityTable<SalaryWageStructureAssignmentRule, 'id'>;

  // 給与明細書マスタ mock（給与・賃金 > 設定 > 給与明細書）。structures は読み取り専用
  paySlipLayouts!: EntityTable<PaySlipLayout, 'id'>;
  paySlipLayoutStructures!: EntityTable<PaySlipLayoutStructure, 'id'>;

  // Salary & Wages アラート mock（将来の給与リスクの事前通知）
  salaryWageAlerts!: EntityTable<SalaryWageAlert, 'id'>;
  constructor() {
    super('DemoDB');
    this.version(1).stores({
      people: 'id, name, department',
      attendanceRecords: 'id, employeeId, date, status, organizationId',
      clockingRecords: 'id, employeeId, timestamp, type, organizationId',
      attendanceAlerts: 'id, employeeId, type, severity, resolved, organizationId',
      attendanceEmployees: 'id, employeeId, department, attendanceStructureId, status, organizationId',
      paySlips: 'id, period, paymentDate, status, countryCode, organizationId',
      paySlipRecords: 'id, paySlipId, employeeId, category, organizationId',
      paySlipFolders: 'id, paySlipId',
      paySlipFiles: 'id, paySlipId, folderId',
      employeePaySlipSummaries: 'id, employeeId, period, organizationId',
      employeePayslipAttendance: 'id, employeeId, period, organizationId',
      paySlipCalculationSnapshots: 'id, employeeId, period, organizationId, snapshotAt',
      engineComponents: 'id, category, countryCode, isDefault, organizationId, validFrom',
      attendanceStructures: 'id, countryCode, isDefault, organizationId, lifecycleStatus',
      payrollStructures: 'id, countryCode, isDefault, organizationId, lifecycleStatus',
      bonusStructures: 'id, countryCode, isDefault, organizationId, lifecycleStatus',
      dashboardMetrics: 'id, period, metricType, organizationId',
      operationPackages: 'id, type, category, status, organizationId',
      payrollGanttSnapshots: 'id, organizationId',
      attendanceGanttSnapshots: 'id, organizationId',
      attendanceOpPackages: 'id, groupIndex, sortOrder',
      attendanceOpPackagePatterns: 'id, packageId, sortOrder',
      attendanceOpPackageInstanceItems: 'id, patternId, sortOrder',
      payrollOpPackages: 'id, groupIndex, sortOrder',
      payrollOpPackagePatterns: 'id, packageId, sortOrder',
      payrollOpPackageInstanceItems: 'id, patternId, sortOrder',
      attendanceRulePolicies: 'id, category, organizationId',
      attendanceOperationActivities: 'id, correlationId, occurredAt, activityType, category, targetEmployeeId, organizationId',
      payrollAlerts: 'id, employeeId, category, type, severity, resolved, period, organizationId',
      payrollOverviewFacts: 'id, employeeId, period, organizationId, officeKey',
      payrollStructureUnits: 'id, structureId, parentUnitId, order',
      payrollStructureItems: 'id, unitId, role, order',
      payrollStructureLogicComponents: 'id, unitId, kind, order',
      attendanceStructureUnits: 'id, structureId, parentUnitId, order',
      attendanceStructureItems: 'id, unitId, role, order',
      attendanceStructureLogicComponents: 'id, unitId, kind, order',
      individualPortalAttendance: 'id, date',
      individualPortalTasks: 'id, sortOrder, dueDate',
      individualPortalApprovals: 'id, sortOrder, approvalDeadline',
      individualPortalRequests: 'id, sortOrder, submittedAt, status'
    });
    this.version(2).stores({
      individualPortalSequenceDefinitions: 'id, kind',
      individualPortalSequenceInstances: 'id, definitionId, taskId, status',
      individualPortalSequenceStepRecords: 'id, instanceId, stepId, status'
    });
    this.version(3).stores({
      individualPortalRequestForms: 'id, category, sortOrder'
    });
    this.version(4).stores({
      individualPortalAiChatSessions: 'id, sortOrder, lastInteractedAt',
      individualPortalUpcomingInteractions: 'id, sortOrder, scheduledAt'
    });
    this.version(5).stores({
      uiPreferences: 'key'
    });

    // v6: re-seed structure detail tables（seed データ変更のたびにバージョンを上げて clear）
    this.version(6).stores({}).upgrade(async trans => {
      await trans.table('payrollStructureUnits').clear();
      await trans.table('payrollStructureItems').clear();
      await trans.table('payrollStructureLogicComponents').clear();
      await trans.table('attendanceStructureUnits').clear();
      await trans.table('attendanceStructureItems').clear();
      await trans.table('attendanceStructureLogicComponents').clear();
    });

    // v7: BonusStructure を廃止し payrollStructures に統合。
    //     bonusStructures テーブルを drop し、payrollStructures に kind インデックスを追加。
    //     payrollStructures を clear して re-seed（kind フィールドを含む新データで上書き）。
    this.version(7).stores({
      bonusStructures: null,
      payrollStructures: 'id, kind, countryCode, isDefault, organizationId, lifecycleStatus'
    }).upgrade(async trans => {
      await trans.table('payrollStructures').clear();
    });

    // v8: AI mock sequence / chat session に `side` フィールドを追加し、
    // individual / cockpit の両サイドを 1 セットのテーブルで保持できるようにする。
    // 既存行は side: "individual" としてバックフィルする。
    this.version(8).stores({
      individualPortalSequenceDefinitions: 'id, kind, side',
      individualPortalAiChatSessions: 'id, sortOrder, lastInteractedAt, side',
      individualPortalUpcomingInteractions: 'id, sortOrder, scheduledAt, side'
    }).upgrade(async tx => {
      await tx.table('individualPortalSequenceDefinitions').toCollection().modify(row => {
        if (!row.side) row.side = 'individual';
      });
      await tx.table('individualPortalAiChatSessions').toCollection().modify(row => {
        if (!row.side) row.side = 'individual';
      });
      await tx.table('individualPortalUpcomingInteractions').toCollection().modify(row => {
        if (!row.side) row.side = 'individual';
      });
    });

    // v9: individualPortalApprovals に status / submittedAt インデックスを追加
    this.version(9).stores({
      individualPortalApprovals: 'id, sortOrder, approvalDeadline, status, submittedAt'
    });

    // v10: Unit を structure 従属から catalog atom に昇格。
    //      - payrollStructure{Units,Items,LogicComponents} → payrollUnit{s,Items,LogicComponents} にリネーム
    //      - 同じく attendanceStructure{Units,Items,LogicComponents} → attendanceUnit{s,Items,LogicComponents}
    //      - 新 unit テーブルは structureId index を廃止 (unit はもう structure に属さない)
    //      - payrollStructures.kind インデックス削除 (bonus structure 廃止のため)
    //      - payrollStructures / attendanceStructures は re-seed (kind drop + unitIds 追加)
    this.version(10).stores({
      payrollStructureUnits: null,
      payrollStructureItems: null,
      payrollStructureLogicComponents: null,
      attendanceStructureUnits: null,
      attendanceStructureItems: null,
      attendanceStructureLogicComponents: null,
      payrollUnits: 'id, countryCode, lifecycleStatus, organizationId, parentUnitId, order',
      payrollUnitItems: 'id, unitId, role, order',
      payrollUnitLogicComponents: 'id, unitId, kind, order',
      attendanceUnits: 'id, countryCode, lifecycleStatus, organizationId, parentUnitId, order',
      attendanceUnitItems: 'id, unitId, role, order',
      attendanceUnitLogicComponents: 'id, unitId, kind, order',
      payrollStructures: 'id, countryCode, isDefault, organizationId, lifecycleStatus'
    }).upgrade(async trans => {
      await trans.table('payrollStructures').clear();
      await trans.table('attendanceStructures').clear();
    });

    // v11: engineComponents を廃止し、payroll / attendance ドメインごとに
    //      item / logicComponent 原子マスタを独立テーブルとして確立。
    //      - engineComponents (横断 atom マスタ) を drop
    //      - payrollItems / payrollLogicComponents / attendanceItems /
    //        attendanceLogicComponents を新設 (category / kind の index は持たない)
    //      - payrollUnitItems / attendanceUnitItems の index を itemId 中心に
    //      - payrollUnitLogicComponents / attendanceUnitLogicComponents の
    //        index を logicComponentId 中心に
    //      - payrollStructures / attendanceStructures の componentIds を
    //        itemIds + logicComponentIds に分割するため re-seed
    //      - 全 unit / unit join 行も seed 再投入対象
    this.version(11).stores({
      engineComponents: null,
      payrollItems: 'id, countryCode, lifecycleStatus, organizationId',
      payrollLogicComponents: 'id, countryCode, lifecycleStatus, organizationId',
      attendanceItems: 'id, countryCode, lifecycleStatus, organizationId',
      attendanceLogicComponents: 'id, countryCode, lifecycleStatus, organizationId',
      payrollUnitItems: 'id, unitId, itemId, role, order',
      payrollUnitLogicComponents: 'id, unitId, logicComponentId, order',
      attendanceUnitItems: 'id, unitId, itemId, role, order',
      attendanceUnitLogicComponents: 'id, unitId, logicComponentId, order'
    }).upgrade(async trans => {
      await trans.table('payrollUnits').clear();
      await trans.table('payrollUnitItems').clear();
      await trans.table('payrollUnitLogicComponents').clear();
      await trans.table('attendanceUnits').clear();
      await trans.table('attendanceUnitItems').clear();
      await trans.table('attendanceUnitLogicComponents').clear();
      await trans.table('payrollStructures').clear();
      await trans.table('attendanceStructures').clear();
    });

    // v12: pay slip / clocking record 詳細画面の alerts / files タブ用に
    //      employee 単位の独立テーブルを追加（mock 専用、絞り込みではなく独立保持）。
    this.version(12).stores({
      employeePaySlipAlerts: 'id, employeeId, category, type, severity, resolved, period, organizationId',
      employeeClockingRecordAlerts: 'id, employeeId, type, severity, resolved, organizationId',
      employeePaySlipDetailFiles: 'id, employeeId, folderType, organizationId',
      employeeClockingRecordFiles: 'id, employeeId, folderType, organizationId'
    });

    // v13: pay slip 詳細画面の employeeId 体系を `emp-s*` に揃えるための reseed。
    //      pay slip 詳細では employeePaySlipSummaries.employeeId = "emp-s01"〜"emp-s05" を使うため、
    //      初版 seed の "emp-001" 系を clear して再投入する。
    this.version(13).stores({}).upgrade(async trans => {
      await trans.table('employeePaySlipAlerts').clear();
      await trans.table('employeePaySlipDetailFiles').clear();
    });

    // v14: chat session タイトル変更 ("入社手続きの相談" → "入社手続き") と、
    //      sequence definition への completionHistory フィールド追加を反映するため、
    //      該当 2 テーブルを clear して再 seed する。
    this.version(14).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
    });

    // v15: 入社手続き mock sequence を 6 → 12 ステップに拡張（text_input / date_input 追加）。
    //      v14 で definitions は clear 済みだが、古い step ID で進行中の instance /
    //      step records が残っていると進行状態がズレるため、それらもまとめて clear する。
    this.version(15).stores({}).upgrade(async trans => {
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v16: 入社イベントを 7 セッションのストーリーに拡張する。
    //      - 新しい kind="hire_event" の definition を 6 件追加 (Session A/C/D/F/H/I)
    //      - 既存 "onboarding" definition を 2026 年版に文言更新 (Session B として再利用)
    //      - 既存の cockpit/individual 重複チャットセッション (cockpit-portal-ai-chat-session-2 /
    //        individual-portal-ai-chat-session-2) を削除し、新ストーリーに置換
    //      影響テーブルを全て clear し、新しい seed を投入する。
    this.version(16).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v17: 入社イベント関連セッションを時系列順で sidebar 上部にまとめ、
    //      その他のセッション (年末調整・デバイス見積もり・社会保険届) を下に配置するため
    //      sortOrder を再採番する。chat session table のみ clear して再 seed させる。
    this.version(17).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
    });

    // v18: Session C (デバイス希望登録) のインタラクション簡素化に伴い、
    //      step を 6 → 5 に再構成 (旧 STEP 1「選択肢を見る」を削除し新 STEP 1 をデバイス比較に)。
    //      step ID と subtitle が変わるため、関連 4 テーブルを clear して再 seed させる。
    this.version(18).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v19: Session C STEP 3 (周辺機器セットの確認 / multi_choice) を
    //      yes_no (追加周辺機器の有無) + field_confirm (貸与機器一覧の確認) の 2 step に分解。
    //      step 数が 5 → 6 に増え step ID/subtitle も変わるため、関連 4 テーブルを再投入させる。
    this.version(19).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v20: Session C STEP 3 の質問を「セカンドディスプレイの希望」に具体化し、
    //      STEP 4 の user_summary_card ラベルも整合させる。文言のみだが definition が
    //      上書きされないので、関連 4 テーブルを clear して再 seed させる。
    this.version(20).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v21: Session C STEP 5 (受け取り方法 / single_choice) を削除し、
    //      STEP 6 (完了) を STEP 5 に繰り上げ。受け取りは出社前提として ai_bubble で案内するのみ。
    //      step 数 6 → 5 / step ID も変わるため、関連 4 テーブルを clear して再 seed させる。
    this.version(21).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v22: Session I (健康診断の受診) を「予約案内 + 予約日入力 + 完了案内」の 3 step に再構成。
    //      システムでの予約取得は行わず、外部クリニックへの電話予約を促す流れに変更。
    //      step 数 5 → 3 / packageTitle・step ID・detailDraft が大きく変わるため再 seed。
    this.version(22).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v23: Session I STEP 1 を「ご案内」から「予約依頼」に書き換え、予約時にクリニック受付へ
    //      伝える情報 (氏名・会社名・入社予定日・健診目的) を明示。packageTitle / shortName /
    //      navLabel / detailDraft 見出しも合わせて変更したため、関連 4 テーブルを再 seed。
    this.version(23).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v24: yes_no answer に optional な phone callout を追加し、Session I STEP 1 で病院予約
    //      連絡先 (03-1234-5678) を answer 領域に prominent に表示。definition を再 seed。
    this.version(24).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v25: 個人ポータルの sidebar から「デバイス購入の見積もり」(session-3) を削除。
    //      seed 側で該当 row を削除済みなので、chat session table を clear して再 seed させる。
    this.version(25).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
    });

    // v26: yes_no callout を rows[] 形式に拡張し、Session A STEP 3 に mail callout を追加
    //      (宛先・件名を chat bubble から answer 領域の callout に移管)。
    //      Session I の phone callout も rows[] 構造に合わせる。definition を再 seed。
    this.version(26).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v27: Session A STEP 4 の answer ラベルと、STEP 5 history の user_summary_card 表示文言を
    //      「既知情報の確認に進む」→「既知の従業員情報の確認に進む」に揃える。definition を再 seed。
    this.version(27).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v28: Session A STEP 4 の answer を single_choice → yes_no に変更し、
    //      「あとで進める」の選択肢を追加。definition を再 seed。
    this.version(28).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v29: pay slip 詳細画面の「PDFでみる」ボタン用に共通 PDF blob を保持する mock テーブルを追加。
    this.version(29).stores({
      paySlipPdfMocks: 'id'
    });

    // v30: グローバル AI チャット用の独立 mock テーブルを追加。
    this.version(30).stores({
      globalAiChatSessions: 'id, updatedAt, createdAt, modelId',
      globalAiChatMessages: 'id, sessionId, createdAt, role',
      globalAiChatModels: 'id, sortOrder'
    });

    // v31: BE (`dresscode-ai-operator`) と契約を合わせるため、グローバル AI チャット
    //      テーブル群を一度 drop する。primary key が `id` → `chatSessionId` / `messageId`
    //      に変わるため、Dexie の自動マイグレーションでは対応できない（テーブルごと作り直し）。
    this.version(31).stores({
      globalAiChatSessions: null,
      globalAiChatMessages: null,
      globalAiChatModels: null
    });

    // v32: 新スキーマで再作成。seed が再投入する。
    //      session: status / preview / currentPagePath / lastMessageAt
    //      message: timestamp / chatSessionId / citations
    //      modelId は廃止（BE にモデル選択の概念無し）。
    this.version(32).stores({
      globalAiChatSessions: 'chatSessionId, lastMessageAt, createdAt, status, userId, organizationId',
      globalAiChatMessages: 'messageId, chatSessionId, timestamp, role',
      globalAiChatModels: 'id, sortOrder'
    });

    // v33: セッション・メッセージは BE（oRPC chatContract）へ移行したためモックテーブルを drop。
    //      モデル選択は BE に概念が無いため globalAiChatModels のみ残していた。
    this.version(33).stores({
      globalAiChatSessions: null,
      globalAiChatMessages: null
    });

    // v34: モデル選択（回答エージェント）を BE（GET /v2/ai-operator/chat/models）へ移行したため
    //      残っていた globalAiChatModels mock テーブルを drop する。
    this.version(34).stores({
      globalAiChatModels: null
    });

    // v35: 個別ポータルに「打刻修正申請」(clock_correction) チャットセッションを追加。
    //      新 kind の definition / instance / step records / session を再 seed するため、
    //      関連 4 テーブルを clear して returning user でも一貫した状態で再投入する。
    this.version(35).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v36: 打刻修正申請の修正理由 step（single_choice）から pill アイコン・補足説明を削除。
    //      definition を再 seed するため関連 4 テーブルを clear する。
    this.version(36).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAiChatSessions').clear();
      await trans.table('individualPortalSequenceDefinitions').clear();
      await trans.table('individualPortalSequenceInstances').clear();
      await trans.table('individualPortalSequenceStepRecords').clear();
    });

    // v37: 個人ポータルの勤怠打刻カードをインタラクティブ化。
    //      IndividualPortalAttendance に clockInAt / clockOutAt を追加したため、
    //      新フィールド (null) を持つ seed で再投入させる。
    this.version(37).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAttendance').clear();
    });

    // v38: 打刻の修正（Time Clock Correction）の一括申請レコードを保持する mock テーブルを追加。
    //      従業員が個人ポータルから出勤・退勤・休憩を修正し、人事承認待ち（pending）として申請する。
    this.version(38).stores({
      timeClockCorrections: 'id, employeeId, date, status, batchId, organizationId'
    });

    // v39: 打刻漏れ（出勤日に片方の打刻が欠落）の行を seed に追加したため、
    //      デモ組織の勤怠・打刻・アラートを一度クリアして再 seed させる（emp-mock は除外）。
    this.version(39).stores({}).upgrade(async trans => {
      const demoOrg = 'org-demo-001';
      const isDemo = (r: {
        organizationId: string;
        employeeId: string;
      }) => r.organizationId === demoOrg && !r.employeeId.startsWith('emp-mock-');
      await trans.table('attendanceRecords').filter(isDemo).delete();
      await trans.table('clockingRecords').filter(isDemo).delete();
      await trans.table('attendanceAlerts').filter(isDemo).delete();
    });

    // v40: 薬局シフト管理 mock 用テーブルを追加（拠点=店舗 / 組織ユニット=エリア /
    //      スタッフ / シフト割当 / シフトアラート）。新規テーブルのみのため upgrade 不要。
    this.version(40).stores({
      pharmacyAreas: 'id, sortOrder',
      pharmacyStores: 'id, areaId, sortOrder',
      shiftStaff: 'id, storeId, position',
      shiftAssignments: 'id, storeId, staffId, date',
      shiftAlerts: 'id, storeId, type, status, severity, date'
    });

    // v41: 個人ポータルの照会リソース mock 用テーブルを追加（拠点 / メンバー資格 /
    //      デバイス / アカウント / 備品 + 保有者マスタ）。新規テーブルのみのため upgrade 不要。
    this.version(41).stores({
      portalPersons: 'id, isMe',
      portalPlaces: 'id, managedByMe, sortOrder',
      memberCertifications: 'id, holderPersonId, sortOrder',
      memberDevices: 'id, holderPersonId, installPlaceId, storagePlaceId, status, sortOrder',
      memberAccounts: 'id, holderPersonId, app, sortOrder',
      memberEquipments: 'id, holderPersonId, placeId, status, sortOrder'
    });

    // v42〜v46: 店長のシフト編成 chat セッション。detailDraft / step 文言の shape を変更したため、
    //           既存の関連行を削除して新 shape で再 seed させる（count guard では既存行を更新できない）。
    const clearShiftRosteringChat = async (trans: Transaction) => {
      const instanceId = 'shift-rostering-inst-shibuya-202605';
      await trans.table('individualPortalSequenceDefinitions').delete('shift-rostering-def-shibuya-202605');
      await trans.table('individualPortalSequenceInstances').delete(instanceId);
      await trans.table('individualPortalSequenceStepRecords').where('instanceId').equals(instanceId).delete();
      await trans.table('individualPortalAiChatSessions').delete('shift-rostering-session-shibuya-202605');
      await trans.table('individualPortalTasks').delete('shift-rostering-task-shibuya-202605');
    };
    this.version(42).stores({}).upgrade(clearShiftRosteringChat);
    this.version(43).stores({}).upgrade(clearShiftRosteringChat);
    this.version(44).stores({}).upgrade(clearShiftRosteringChat);
    this.version(45).stores({}).upgrade(clearShiftRosteringChat);
    this.version(46).stores({}).upgrade(clearShiftRosteringChat);

    // v47: IndividualPortalAttendance を clockInAt / clockOutAt から punches 配列へ移行。
    //      旧フィールドを持つレコードは punches を欠くため、clear して新 seed で再投入させる
    //      （v37 で clockInAt / clockOutAt を導入したときと同じ理由）。
    this.version(47).stores({}).upgrade(async trans => {
      await trans.table('individualPortalAttendance').clear();
    });

    // v48: ソフトウェア（memberAccounts）に category を追加したため、既存行を削除して
    //      新 shape（category 付き）で再 seed させる（count guard では既存行を更新できない）。
    this.version(48).stores({}).upgrade(async trans => {
      await trans.table('memberAccounts').clear();
    });

    // v49: メンバー資格（memberCertifications）に自分の保有資格（医薬品登録販売者 /
    //      薬剤師免許）を追加したため、既存行を削除して新 seed で再投入させる
    //      （count guard では既存行に新レコードを足せない）。
    this.version(49).stores({}).upgrade(async trans => {
      await trans.table('memberCertifications').clear();
    });

    // v50: 個人ポータルの「自分」を三浦 智子 → 田中 美咲（misaki.tanaka@example.com）に統一。
    //      出勤簿 self=emp-001 / 給与明細 self=emp-s01 の氏名は各テーブルに denormalize
    //      されており count guard では更新できないため、該当テーブルを clear して再 seed させる。
    //      勤怠系はデモ組織の非 emp-mock 行のみ削除し、attendanceEmployees も含めることで
    //      seedAttendanceData の bulkAdd 衝突を避け、築地店（emp-tsukiji-*）の guard もリセットする。
    this.version(50).stores({}).upgrade(async trans => {
      const demoOrg = 'org-demo-001';
      const isDemo = (r: {
        organizationId: string;
        employeeId: string;
      }) => r.organizationId === demoOrg && !r.employeeId.startsWith('emp-mock-');
      await trans.table('attendanceRecords').filter(isDemo).delete();
      await trans.table('clockingRecords').filter(isDemo).delete();
      await trans.table('attendanceAlerts').filter(isDemo).delete();
      await trans.table('attendanceEmployees').filter(isDemo).delete();
      await trans.table('employeePaySlipSummaries').clear();
      await trans.table('employeePaySlipAlerts').clear();
      await trans.table('employeeClockingRecordAlerts').clear();
      await trans.table('employeePaySlipDetailFiles').clear();
      await trans.table('employeeClockingRecordFiles').clear();
    });

    // v51: 賃金台帳（Wage Ledger）mock 用テーブルを追加。新規テーブルのみのため upgrade 不要。
    this.version(51).stores({
      wageLedgerLedgers: 'id, memberId, wageType, status, source, organizationId',
      wageLedgerEntries: 'id, ledgerId, memberId, periodStart, organizationId',
      wageLedgerEarnings: 'id, entryId, wageClass',
      wageLedgerDeductions: 'id, entryId',
      wageLedgerRulePolicies: 'id, category, sortOrder, organizationId',
      wageLedgerAlerts: 'id, memberId, type, severity, resolved, period, organizationId',
      wageLedgerOperationActivities: 'id, correlationId, occurredAt, activityType, category, targetMemberId, organizationId'
    });

    // v52: 賃金台帳の賃金形態区分を 月給/日給/時給 → 月次給与制/シフトバイト時給制/
    //      フレックス年俸制 に変更したため、既存行を削除して新 shape で再 seed させる
    //      （count guard では既存行を更新できない）。
    this.version(52).stores({}).upgrade(async trans => {
      await trans.table('wageLedgerLedgers').clear();
      await trans.table('wageLedgerEntries').clear();
      await trans.table('wageLedgerEarnings').clear();
      await trans.table('wageLedgerDeductions').clear();
    });
    this.version(53).stores({
      workExperienceCompanies: 'id'
    });
    this.version(54).stores({
      deviceLicenses: 'id, deviceId, sortOrder'
    });
    this.version(55).stores({
      licenseViolationAlerts: 'id, category, sortOrder'
    });
    this.version(56).stores({
      licenseMembers: 'id, sortOrder',
      licenseActivities: 'id, sortOrder',
      licenseAssignmentRules: 'id, sortOrder',
      licenseSeats: 'id, sortOrder',
      licenseRenewals: 'id, sortOrder'
    });

    // v57: 賃金台帳（Wage Ledger）のデモ実装を削除したため、v51 で追加した
    //      mock テーブル群を drop する。
    this.version(57).stores({
      wageLedgerLedgers: null,
      wageLedgerEntries: null,
      wageLedgerEarnings: null,
      wageLedgerDeductions: null,
      wageLedgerRulePolicies: null,
      wageLedgerAlerts: null,
      wageLedgerOperationActivities: null
    });
    this.version(58).stores({
      payrollInputSnapshotFields: 'id, sourceCategory, sortOrder'
    });

    // v59: 月次給与（Enterprise）の unit 01「入退社・異動処理」を nested stage 化し、
    //      実処理を子 unit 3 件 (01a/01b/01c) へ移した。旧 unit 01 直下の
    //      unit item / logic component join 行は seed から消えるが bulkPut では
    //      削除されないため、3 テーブルを clear して再投入する。
    this.version(59).stores({}).upgrade(async trans => {
      await trans.table('payrollUnits').clear();
      await trans.table('payrollUnitItems').clear();
      await trans.table('payrollUnitLogicComponents').clear();
    });

    // v61: nested stage と substage の区別を廃止し、substage を「配下 unit を持つ
    //      入れ物」に統一した。月次給与（Enterprise）では
    //      - substage 32 件を output 単位の unit へ分割し、join 行を配下 unit へ付け替え
    //      - 基準日スナップショット logic を unit 01a → 新設の 01d へ移動
    //      - unit 01c / 03 の output を専用 item に差し替え
    //      あわせて ps-monthly-enterprise 以外の payroll / attendance structure seed を削除。
    //      seed から消えた structure / unit / join 行は bulkPut では残るため、
    //      両エンジンの structure と unit 系テーブルを clear して再投入する。
    //      （v60 は開発中に一度だけローカルへ適用された中間版。手元 DB が v60 で
    //        止まっているケースを拾うため、番号を飛ばして v61 で確定させる）
    this.version(61).stores({}).upgrade(async trans => {
      await trans.table('payrollStructures').clear();
      await trans.table('payrollUnits').clear();
      await trans.table('payrollUnitItems').clear();
      await trans.table('payrollUnitLogicComponents').clear();
      await trans.table('attendanceStructures').clear();
      await trans.table('attendanceUnits').clear();
      await trans.table('attendanceUnitItems').clear();
      await trans.table('attendanceUnitLogicComponents').clear();
    });

    // v62: Salary & Wages（給与台帳カタログ E1〜E4c）mock 用テーブルを追加。
    //      E4b/E4c は国制度をカラムに焼き込まず 1:N の行として持つ（設計原則6）。
    //      新規テーブルのみのため upgrade 不要。
    //      （topic ブランチ統合時、salary 側の元 v59 が payroll-engine 側の v59 と
    //        衝突したため v62 へ繰り下げた。v60 は使用済み番号のため飛ばす）
    this.version(62).stores({
      salaryWageRecords: 'id, employeeId, paymentDate, payPeriodStart, runType, status, countryCode, payGroupId, organizationId',
      salaryWageLines: 'id, recordId, lineType, itemCode',
      salaryWageAttendanceSnapshots: 'id, recordId',
      salaryWageTaxBases: 'id, recordId',
      salaryWageJurisdictionTaxLines: 'id, recordId, jurisdictionType, taxCode',
      salaryWageSchemeContributions: 'id, recordId, schemeCode, schemeCategory'
    });

    // v63: E2V（賃金項目 標準語彙）テーブルを追加。個人給与明細詳細が明細行の
    //      表示名・課税区分の既定値をコードから引くために使う。新規テーブルのみのため
    //      upgrade 不要。
    this.version(63).stores({
      salaryWageItemVocabulary: 'code, lineType, category'
    });

    // v64: 事業場マスタ mock 用テーブルを追加。新規テーブルのみのため upgrade 不要。
    this.version(64).stores({
      establishments: 'id, code, priority',
      establishmentOverrides: 'id, employeeId, establishmentId',
      establishmentPlaces: 'id, prefectureCode, sortOrder',
      establishmentOrgUnits: 'id, parentId, sortOrder',
      // isSeconded は boolean で IndexedDB のキーにできないため索引に含めない
      establishmentEmployees: 'id, orgUnitId, sortOrder'
    });

    // v65: E5（差引支給額の振り分け）テーブルを追加。複数口座への振り分けを行の増減で
    //      表現するため 1:N。新規テーブルのみのため upgrade 不要。
    //      （topic 統合時、事業場マスタ側の v64 と衝突したため v65 へ繰り下げた）
    this.version(65).stores({
      salaryWagePaymentAllocations: 'id, recordId'
    });

    // v66: payroll-core の業務パッケージ一覧から「給与計算締め」「年末調整」を削除し、
    //      「給与計算」「給与支払」「賞与支払」を追加した。seed は
    //      payrollOpPackages.count() === 0 のときだけ走るため、旧パッケージが残った
    //      ローカル DB を作り直せるよう 3 テーブルを clear する。
    this.version(66).stores({}).upgrade(async trans => {
      await trans.table('payrollOpPackages').clear();
      await trans.table('payrollOpPackagePatterns').clear();
      await trans.table('payrollOpPackageInstanceItems').clear();
    });

    // v67: Salary & Wages のルール&ポリシー（給与計算体系アサインメントルール）mock
    //      テーブルを追加。適用先の給与計算体系は payrollStructures を参照する。
    //      新規テーブルのみのため upgrade 不要。
    this.version(67).stores({
      salaryWageStructureAssignmentRules: 'id, payrollStructureId, sortOrder'
    });

    // v68: 給与明細書マスタ mock 用テーブルを追加。新規テーブルのみのため upgrade 不要。
    this.version(68).stores({
      paySlipLayouts: 'id, name',
      paySlipLayoutStructures: 'id, sortOrder'
    });

    // v69: Salary & Wages のアラート mock テーブルを追加。新規テーブルのみのため
    //      upgrade 不要。resolved は boolean で IndexedDB のキーにできないため索引外。
    //      （topic 統合時、給与明細書マスタ側の v68 と衝突したため v69 へ繰り下げた）
    this.version(69).stores({
      salaryWageAlerts: 'id, item, severity, scope'
    });
  }
}
export const db = new DemoDatabase();